// @flow
import React from 'react';
import type {Node} from 'react';
import {Link} from '@homeaway/react-localization-components';
import {
    AspectRatioCard,
    AspectRatioCardImage
} from '@homeaway/react-aspect-ratio-card';
import {TripBoardButton} from '@homeaway/react-trip-board-button';
import {FormattedMessage} from 'react-intl';
import PriceSummary from '@homeaway/react-price-summary';
import Rating from '../../Rating/Rating';
import type {PriceSummaryShape} from '../../Price/Price';
import {ASPECT_RATIO} from '../constants';
import './PropertyCard.less';

type PropertyCardImagesShape = {
    uri: string
};

type PropertyCardMetadataShape = {
    headline: string
};

export type PropertyCardListingShape = {
    averageRating: number,
    reviewCount: number,
    listingId: string,
    detailPageUrl: string,
    bedrooms: number,
    sleeps: number,
    images: PropertyCardImagesShape[],
    priceSummary: PriceSummaryShape,
    propertyMetadata: PropertyCardMetadataShape,
    starSize: string
};

type Props = {
    listing: PropertyCardListingShape,
    aspectRatio: '16x9' | '3x2' | '4x3' | '1x1' | '3x4',
    showTripBoardButton: boolean,
    isMobile: boolean
};

const PropertyCard = (props: Props) => {
    const {listing, aspectRatio, showTripBoardButton, isMobile} = props;
    const {
        detailPageUrl,
        listingId,
        averageRating,
        reviewCount,
        images,
        bedrooms,
        sleeps,
        priceSummary,
        propertyMetadata
    } = listing;

    listing.starSize = '12';

    return (
        <div className="PropertyCard">
            {showTripBoardButton && (
                <div className="PropertyCard__tripboardButton">
                    <TripBoardButton
                        expandedButton={false}
                        listingId={listingId}
                        averageRating={averageRating}
                        reviewCount={reviewCount}
                        tabIndex={-1}
                        thumbnailUrl={images && images[0] && images[0].uri}
                        useNativeEventListener={isMobile}
                        redOnHover
                        actionlocation={'search'}
                    />
                </div>
            )}
            <Link
                className="PropertyCard__anchor"
                href={detailPageUrl}
                target="_blank"
            >
                <AspectRatioCard aspectRatio={aspectRatio} hasHover={false}>
                    <AspectRatioCardImage
                        data-slot="figure"
                        alt={propertyMetadata && propertyMetadata.headline}
                        imageUri={images && !!images.length && images[0].uri}
                    />
                    <div className="PropertyCard__content" data-slot="content">
                        {!!bedrooms && (
                            <div className="PropertyCard__details">
                                <span>
                                    <FormattedMessage
                                        id="bedroomsAbbreviationParam"
                                        description="bedrooms abbreviation label"
                                        defaultMessage="BR"
                                        values={{
                                            bedrooms
                                        }}
                                    >
                                        {(txt: Node): Node => txt}
                                    </FormattedMessage>
                                </span>
                                <span>
                                    <FormattedMessage
                                        id="sleepsAbbreviation"
                                        description="sleeps label"
                                        defaultMessage="Sleeps {value}"
                                        values={{
                                            value: sleeps
                                        }}
                                    >
                                        {(txt: Node): Node => txt}
                                    </FormattedMessage>
                                </span>
                            </div>
                        )}
                        {!!priceSummary && (
                            <div className="PropertyCard__price">
                                <PriceSummary priceSummary={priceSummary} />
                            </div>
                        )}
                        {!!reviewCount && (
                            <div className="PropertyCard__starRating">
                                <Rating listing={listing} isDualPrice />
                            </div>
                        )}
                    </div>
                </AspectRatioCard>
            </Link>
        </div>
    );
};

PropertyCard.defaultProps = {
    listing: {},
    aspectRatio: ASPECT_RATIO,
    showTripBoardButton: true
};

export default PropertyCard;
